import {
  configureModuleStorage,
  configureServerClock,
  type CreateArtisDeps,
  serverClock,
  type ShellWorkerProxy,
} from "@artis/sdk";
import { type ArtisAppProviderProps, ArtisAppProvider as SdkArtisAppProvider } from "@artis/sdk-react";
import { logger } from "@artis/logger";
import { useGraphQLClient } from "@package/graphql/react";
import { type ReactNode, useCallback, useEffect, useMemo } from "react";

import { useFeatureFlags } from "../context/flags";
import { createHasuraStreamTransport } from "../data/module-storage/hasuraStreamTransport";
import { startServerClockSync, workerClockTransport } from "../webapp/calcs/lib/serverClock";
import { useCalcWorker } from "../webapp/calcs/react/hooks";
import { useUserId } from "../context/auth/hooks";

/** Identifies this tab to the shared calc worker; shell subscriptions and update streams are keyed by it. */
const shellClientId = crypto.randomUUID();

/** Same value as `X-Artis-Version` and the o11y `version` field; the dev server serves a matching version.json. */
const deployment: ArtisAppProviderProps["deployment"] = {
  current: import.meta.env.VITE_GIT_TAG || import.meta.env.VITE_GIT_SHA,
  onError: (error) => logger.warn("Deployment version check failed", { error }),
};

export const ArtisAppProvider = ({ children }: { children: ReactNode }) => {
  const worker = useCalcWorker();
  const [artisFlag] = useFeatureFlags("artis-api");
  const enabled = Boolean(artisFlag);
  const userId = useUserId();
  const client = useGraphQLClient();

  const transport = useMemo(() => createHasuraStreamTransport(client, userId), [client, userId]);

  useEffect(() => {
    if (userId) configureModuleStorage({ userId, transport });
  }, [userId, transport]);

  useEffect(() => {
    configureServerClock({ fetchServerTime: workerClockTransport(() => worker.api.clockNow()) });
    // Short retry: the first samples fail until the worker's own HTTP sync lands, and the postMessage hop is cheap.
    const stopSync = startServerClockSync(serverClock, { retryMs: 2_000 });
    const offReady = worker.onReady(() => serverClock.sync().catch(() => {}));
    return () => {
      stopSync();
      offReady();
    };
  }, [worker]);

  const proxy = useMemo<ShellWorkerProxy>(
    () => ({
      getVersion: () => worker.api.shellGetVersion(),
      setSubscriptions: (subs) => worker.api.shellSetSubscriptions(shellClientId, subs),
      onUpdate: (cb) => worker.api.shellOnUpdate(shellClientId, cb),
      onEod: (cb) => worker.api.shellOnEod(shellClientId, cb),
      onPages: (cb) => worker.api.shellOnPages(shellClientId, cb),
      pagesCommand: (command) => worker.api.shellPagesCommand(shellClientId, command),
    }),
    [worker],
  );

  const onWorkerReady = useCallback<NonNullable<CreateArtisDeps["onWorkerReady"]>>(
    (cb) =>
      worker.onReady(async () => {
        await worker.api.shellSetEnabled(shellClientId, true);
        await cb();
      }),
    [worker],
  );

  const onBeforeBootstrap = useCallback(() => worker.api.shellSetEnabled(shellClientId, true), [worker]);
  const onAfterTeardown = useCallback(() => worker.api.shellSetEnabled(shellClientId, false), [worker]);

  useEffect(() => {
    const release = (event: PageTransitionEvent) => {
      if (event.persisted) return;
      void worker.api.shellSetEnabled(shellClientId, false);
    };
    window.addEventListener("pagehide", release);
    return () => window.removeEventListener("pagehide", release);
  }, [worker]);

  return (
    <SdkArtisAppProvider
      enabled={enabled}
      worker={proxy}
      onWorkerReady={onWorkerReady}
      onBeforeBootstrap={onBeforeBootstrap}
      onAfterTeardown={onAfterTeardown}
      deployment={deployment}
    >
      {children}
    </SdkArtisAppProvider>
  );
};
